/**
 * VRG-761 — delete the BOX, keep the chips.
 *
 * This modal used to say "Deleting this package will remove it and its tags from Vera", and that
 * was accurate: `tags.package_id` was ON DELETE CASCADE, so one click destroyed every chip in the
 * box, including encoded ones that were the subject of a certificate's provenance.
 *
 * It now asks a narrower question. The box is always deleted. Tags inside are DETACHED and kept.
 * The operator may additionally tick "also delete the unused tags" — tags with no trace of ever
 * having been used — and used tags are never deleted whatever is ticked.
 *
 * ⚠️ THE COUNT COMES FROM THE SERVER and must keep coming from the server
 * (`usePackageDeletePreview` -> `GET /packages/:id/delete-preview`). The endpoint and the delete
 * path apply the same `UNUSED_TAG_CONDITION` fragment, so the number beside the checkbox is the
 * number of rows the button removes. Computing "unused" here instead would make the modal a
 * second, independent answer to that question, and the two would drift.
 */
import React, { useEffect, useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { usePackageDelete, usePackageDeletePreview } from 'hooks'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Alert, CheckBox, Skeleton } from '@repo/ui'

interface DeletePackageModalProps {
  packageObj: any
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const DeletePackageModal = (props: DeletePackageModalProps) => {
  const { packageObj, open, onClose = () => {}, refetch } = props

  const { open: notification } = useSnackbar()

  const packageId = packageObj?.id ? String(packageObj.id) : null

  const deleteMutation = usePackageDelete()
  const { data: preview, isLoading: isPreviewLoading } = usePackageDeletePreview(packageId, {
    enabled: open,
  })

  const [alsoDeleteUnused, setAlsoDeleteUnused] = useState(false)

  // Default OFF on every open, never remembered. The destructive half of this dialog is opt-in
  // per package: a tick carried over from the last box the operator deleted would destroy chips
  // nobody looked at.
  useEffect(() => {
    if (open) setAlsoDeleteUnused(false)
  }, [open, packageId])

  const tagsUnused = preview?.tagsUnused ?? 0
  const tagsUsed = preview?.tagsUsed ?? 0
  const tagsTotal = preview?.tagsTotal ?? 0

  const handleDeletePackage = () => {
    if (!packageId) return
    deleteMutation.mutate(
      { id: packageId, deleteUnusedTags: alsoDeleteUnused },
      {
        onSuccess: () => {
          handleClose()
          refetch()
        },
        onError: (error: any) => {
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      title="Delete package"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={() => handleClose()}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={() => handleDeletePackage()}
            // Disabled while the preview loads: the button's consequence depends on a count that
            // is not on screen yet, so there is a moment where a click would mean something the
            // operator has not been told.
            disabled={deleteMutation.isPending || isPreviewLoading}
          >
            Delete
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {`Are you sure you want to delete package "PKG-${packageObj?.id}"?`}
        </Typography>

        {isPreviewLoading ? (
          // Skeleton, not a spinner, and sized to the block it replaces so the dialog does not
          // resize under the cursor when the counts arrive (§19).
          <Skeleton lines={2} />
        ) : (
          <>
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {tagsTotal === 0
                ? 'This package is empty.'
                : tagsUsed > 0
                  ? `This package holds ${tagsTotal} ${tagsTotal === 1 ? 'tag' : 'tags'}. ${tagsUsed} of them ${tagsUsed === 1 ? 'has' : 'have'} been used, so ${tagsUsed === 1 ? 'it stays' : 'they stay'} in Vera and ${tagsUsed === 1 ? 'is' : 'are'} simply no longer in a box.`
                  : `This package holds ${tagsTotal} ${tagsTotal === 1 ? 'tag' : 'tags'}, none of which has been used yet.`}
            </Typography>

            {tagsUnused > 0 && (
              <CheckBox
                name="alsoDeleteUnused"
                label={`Also delete the ${tagsUnused} unused ${tagsUnused === 1 ? 'tag' : 'tags'} in this package`}
                checked={alsoDeleteUnused}
                onChange={event => setAlsoDeleteUnused(event.target.checked)}
                disabled={deleteMutation.isPending}
              />
            )}

            {/*
              The clause list in this sentence mirrors `UNUSED_TAG_CONDITION` in
              apps/api/src/database/tag-unused.ts, which is the definition. If a clause is ever
              added or removed there, this copy has to follow — it is the only place the operator
              is told what "unused" means, and a stale list here is a promise the server does not
              keep. It is prose rather than a shared constant because the predicate lives in the
              API and this app cannot import from it.
            */}
            {alsoDeleteUnused && (
              <Alert
                severity="warning"
                description={`${tagsUnused} ${tagsUnused === 1 ? 'tag' : 'tags'} will be permanently deleted. There is no undo. A tag counts as unused only if it has never been encoded, never been linked to a certificate, never been scanned, and has never left Vera.`}
                sx={{ padding: '12px 24px' }}
              />
            )}
          </>
        )}
      </Stack>
    </Modal>
  )
}
